import { useEffect, useState } from "react";
import { Box, Typography, keyframes } from "@mui/material";
import {
  WarningRounded as WarningRoundedIcon,
  NotificationsRounded as NotificationsRoundedIcon,
  OndemandVideoRounded as OndemandVideoRoundedIcon,
  ScheduleRounded as ScheduleRoundedIcon,
  FiberManualRecordRounded as FiberManualRecordRoundedIcon,
  SportsBasketballRounded as SportsBasketballRoundedIcon,
} from "@mui/icons-material";

/** iconType -> icon component + default tint, so callers pass data, not components. */
const ICON_TYPES = {
  alert: { Icon: WarningRoundedIcon, color: "#f5a623" },
  reminder: { Icon: NotificationsRoundedIcon, color: "#0a0a0b" },
  video: { Icon: OndemandVideoRoundedIcon, color: "#0a0a0b" },
  schedule: { Icon: ScheduleRoundedIcon, color: "#0a0a0b" },
  live: { Icon: FiberManualRecordRoundedIcon, color: "#eb5757" },
  score: { Icon: SportsBasketballRoundedIcon, color: "#0a0a0b" },
};

/** Drifting color orbs behind the glass card (personalization-page palette). */
const ORBS = [
  { color: "#34d399", top: "-32%", left: "-12%" },
  { color: "#22d3ee", top: "-18%", left: "42%" },
  { color: "#818cf8", top: "28%", left: "56%" },
  { color: "#e879f9", top: "40%", left: "-4%" },
  { color: "#fb923c", top: "0%", left: "24%" },
];

/** Default updates the card cycles through on the homepage hero. */
const DEFAULT_NOTIFICATIONS = [
  { dow: "SAT", day: "8", iconType: "alert", title: "Delayed: Braves at Marlins", time: "now", body: "Now starts 5:00 PM" },
  { dow: "FRI", day: "12", iconType: "video", title: "New episode: Stranger Things", time: "now", body: "Season 5, Episode 3 is out now on Netflix" },
  { dow: "TUE", day: "5", iconType: "reminder", title: "Lakers at Warriors", time: "5m", body: "Starts in 30 minutes · Watch on ABC" },
  { dow: "SUN", day: "14", iconType: "schedule", title: "Time change: Monaco Grand Prix", time: "now", body: "Moved up to 8:00 AM · Watch on ESPN" },
  { dow: "SAT", day: "20", iconType: "live", title: "Live now: Champions League Final", time: "now", body: "Real Madrid vs Bayern · Watch on Paramount+" },
  { dow: "SUN", day: "6", iconType: "reminder", title: "Cowboys at Eagles", time: "1h", body: "Sunday Night Football · Kickoff on NBC" },
];

/** How long each notification stays before morphing to the next. */
const MORPH_MS = 4200;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(-8px) scale(0.99); }
  to { opacity: 1; transform: none; }
`;

/** Apple Calendar style app icon: red weekday over a black day number. */
const AppIcon = ({ dow, day }) => (
  <Box
    sx={{
      flex: "0 0 auto",
      width: 52,
      height: 52,
      borderRadius: "13px",
      backgroundColor: "#fff",
      boxShadow: "0 2px 6px rgba(0,0,0,0.14)",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      overflow: "hidden",
    }}
  >
    <Typography sx={{ fontSize: "0.58rem", fontWeight: 800, letterSpacing: "0.05em", color: "#eb5757" }}>
      {dow}
    </Typography>
    <Typography sx={{ fontSize: "1.55rem", fontWeight: 700, lineHeight: 1, mt: "-1px", color: "#111" }}>
      {day}
    </Typography>
  </Box>
);

/**
 * Frosted-glass push notification that morphs through the kinds of live updates
 * a followed calendar delivers (delays, time changes, new episodes, reminders),
 * with drifting color orbs behind it. Shared by the discovery hero and the
 * league case-study hero.
 *
 * @param {object} props
 * @param {Array<{dow,day,iconType,title,time,body}>} [props.notifications] - Update list; defaults to the homepage set.
 * @param {number} [props.maxWidth] - Card max width in px.
 */
const LiveUpdateNotification = ({ notifications = DEFAULT_NOTIFICATIONS, maxWidth = 500 }) => {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const id = setInterval(
      () => setIndex((v) => (v + 1) % notifications.length),
      MORPH_MS
    );
    return () => clearInterval(id);
  }, [notifications.length]);

  const n = notifications[index];
  const { Icon: NotifIcon, color: iconColor } = ICON_TYPES[n.iconType] || ICON_TYPES.reminder;

  return (
    <Box sx={{ position: "relative", width: "100%", maxWidth: `${maxWidth}px`, mx: "auto" }}>
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          inset: "-14px",
          borderRadius: "40px",
          overflow: "hidden",
          zIndex: 0,
          maskImage: "radial-gradient(closest-side, #000 62%, transparent 100%)",
          WebkitMaskImage: "radial-gradient(closest-side, #000 62%, transparent 100%)",
        }}
      >
        {ORBS.map((o, i) => (
          <Box
            key={i}
            sx={{
              position: "absolute",
              top: o.top,
              left: o.left,
              width: "58%",
              aspectRatio: "1",
              borderRadius: "50%",
              background: `radial-gradient(circle, ${o.color} 0%, ${o.color}00 70%)`,
              // Static on purpose: animating a blurred layer re-rasterizes every
              // frame and crash-loops iOS Safari's compositor.
              filter: "blur(40px)",
              opacity: 0.85,
            }}
          />
        ))}
      </Box>

      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          display: "flex",
          gap: 1.75,
          alignItems: "flex-start",
          textAlign: "left",
          p: "15px 17px",
          borderRadius: "22px",
          backgroundColor: "rgba(255,255,255,0.55)",
          backdropFilter: "blur(22px) saturate(160%)",
          WebkitBackdropFilter: "blur(22px) saturate(160%)",
          border: "1px solid rgba(255,255,255,0.75)",
          boxShadow: "0 16px 44px rgba(80,80,140,0.20), inset 0 1px 0 rgba(255,255,255,0.7)",
        }}
      >
        <AppIcon dow={n.dow} day={n.day} />
        <Box
          key={index}
          sx={{
            flex: 1,
            minWidth: 0,
            pt: "2px",
            animation: `${fadeIn} 0.5s cubic-bezier(0.2,0.7,0.2,1)`,
            "@media (prefers-reduced-motion: reduce)": { animation: "none" },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.9 }}>
            <NotifIcon sx={{ fontSize: "1.2rem", color: iconColor, flex: "0 0 auto" }} />
            <Typography
              sx={{
                flex: 1,
                minWidth: 0,
                fontSize: "1.12rem",
                fontWeight: 700,
                letterSpacing: "-0.01em",
                lineHeight: 1.2,
                color: "#0a0a0b",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {n.title}
            </Typography>
            <Typography sx={{ flex: "0 0 auto", fontSize: "0.85rem", fontWeight: 500, color: "#6a6f7c" }}>
              {n.time}
            </Typography>
          </Box>
          <Typography
            sx={{
              mt: 0.5,
              fontSize: "1rem",
              lineHeight: 1.35,
              color: "#43485a",
              // Fixed two-line box so a per-slide height change doesn't shift the page.
              height: "2.7em",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {n.body}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default LiveUpdateNotification;
